/**
 * calendarStore — Zustand store for calendar month navigation.
 *
 * Holds the currently viewed {year, month} as ephemeral UI state.
 * Month is 0-indexed (JS Date convention: Jan=0, Dec=11).
 *
 * Initialization uses local-time getters (getFullYear/getMonth) —
 * consistent with getTodayISO() convention to show the user's local
 * calendar date, not UTC. See Pitfall 5 in 03-RESEARCH.md.
 *
 * Year-boundary wrapping uses the JS Date constructor naturally:
 *   new Date(2026, 12, 1) → Jan 2027  (Dec → Jan rolls year forward)
 *   new Date(2026, -1, 1)  → Dec 2025 (Jan → Dec rolls year back)
 * No manual modulo needed.
 */
import { create } from 'zustand';

interface CalendarState {
  /** Currently viewed year (e.g. 2026) */
  year: number;
  /** Currently viewed month, 0-indexed (Jan=0, Dec=11) */
  month: number;
  /** Navigate to the previous month, wrapping year boundaries correctly */
  prevMonth: () => void;
  /** Navigate to the next month, wrapping year boundaries correctly */
  nextMonth: () => void;
  /** Jump back to the current (local) month */
  goToToday: () => void;
  /** Whether the left summary sidebar is visible (calendar view). */
  summaryOpen: boolean;
  /** Toggle the summary sidebar open/closed. */
  toggleSummary: () => void;
  /** Close the summary sidebar (used by the native Android back-button handler). */
  closeSummary: () => void;
  /** Currently selected date (YYYY-MM-DD) — drives the inline DayDetailPanel below the grid. */
  selectedDate: string;
  /** Select a date to display in the inline DayDetailPanel. */
  setSelectedDate: (d: string) => void;
  /** Whether the mobile draggable bottom sheet (DayDetailPanel) is visible. */
  dayDetailOpen: boolean;
  /** Select a date AND open the mobile bottom sheet to show its details. */
  openDayDetail: (d: string) => void;
  /** Close the mobile bottom sheet. */
  closeDayDetail: () => void;
  /** Whether the in-place add-entry dialog is open — also driven by the iOS home-screen quick action. */
  addEntryOpen: boolean;
  /** Open the add-entry dialog (desktop button, mobile FAB, and the iOS quick action). */
  openAddEntry: () => void;
  /** Close the add-entry dialog. */
  closeAddEntry: () => void;
}

/** Derive YYYY-MM-DD using local getters (Pitfall 1: avoids UTC off-by-one from toISOString). */
function getLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The Tailwind `lg` breakpoint as a media query — the single source of truth for summaryOpen. */
export const LG_MEDIA_QUERY = '(min-width: 1024px)';

/** matchMedia for lg, or null where there is no window/matchMedia (SSR, node tests). */
function lgMediaQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || !window || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(LG_MEDIA_QUERY);
}

export const useCalendarStore = create<CalendarState>((set) => ({
  // Local-time getters — never getUTCFullYear/getUTCMonth (Pitfall 5)
  year: new Date().getFullYear(),
  month: new Date().getMonth(),

  prevMonth: () =>
    set((s) => {
      // JS Date naturally wraps: new Date(2026, -1, 1) → Dec 2025
      const d = new Date(s.year, s.month - 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    }),

  nextMonth: () =>
    set((s) => {
      // JS Date naturally wraps: new Date(2026, 12, 1) → Jan 2027
      const d = new Date(s.year, s.month + 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    }),

  goToToday: () => {
    // Local-time getters — show the user's current calendar month (Pitfall 5)
    const now = new Date();
    set({ year: now.getFullYear(), month: now.getMonth() });
  },

  // Open by default at lg+ (desktop, iPad landscape, unfolded iPhone Duo), collapsed below lg
  // so the summary sits behind the header hamburger rather than taking vertical space.
  // Initialised from the lg media query; bindSummaryToViewport() keeps it in sync when the
  // viewport crosses lg mid-session (fold/unfold, rotation, window resize).
  summaryOpen: lgMediaQuery()?.matches ?? true,

  toggleSummary: () => set((s) => ({ summaryOpen: !s.summaryOpen })),

  closeSummary: () => set({ summaryOpen: false }),

  // Local-time getters — never toISOString (Pitfall 1)
  selectedDate: getLocalDateStr(new Date()),

  setSelectedDate: (d) => set({ selectedDate: d }),

  dayDetailOpen: false,

  openDayDetail: (d) => set({ selectedDate: d, dayDetailOpen: true }),

  closeDayDetail: () => set({ dayDetailOpen: false }),

  addEntryOpen: false,

  openAddEntry: () => set({ addEntryOpen: true }),

  closeAddEntry: () => set({ addEntryOpen: false }),
}));

/** Active viewport binding (module-level guard so the store subscribes only once). */
let viewportBinding: { mql: MediaQueryList; listener: (e: MediaQueryListEvent) => void; refs: number } | null =
  null;

/**
 * Keep summaryOpen in sync with the lg breakpoint for the whole session. Crossing below lg
 * closes the drawer (even if the user had opened it); crossing to lg+ re-opens it (the desktop
 * default). Pure media-query driven: no orientation / userAgent / device-idiom checks.
 *
 * Called once at boot from main.tsx. Idempotent: repeat calls share the one subscription.
 * Returns an unsubscribe; the listener is removed when every caller has unsubscribed.
 */
export function bindSummaryToViewport(mql?: MediaQueryList): () => void {
  if (!viewportBinding) {
    const target = mql ?? lgMediaQuery();
    if (!target) return () => {};
    const listener = (e: MediaQueryListEvent) => useCalendarStore.setState({ summaryOpen: e.matches });
    target.addEventListener('change', listener);
    viewportBinding = { mql: target, listener, refs: 0 };
  }
  const binding = viewportBinding;
  binding.refs += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    binding.refs -= 1;
    if (binding.refs <= 0 && viewportBinding === binding) {
      binding.mql.removeEventListener('change', binding.listener);
      viewportBinding = null;
    }
  };
}
